import { useEffect, useMemo, useState } from 'react';
import { useRumError } from 'services/observability/datadog';
import { transformToBaseOptions } from 'ui/selects/utils';
import { RestQueryHookOptions, useRestQuery } from 'utils/hooks/api/useRestQuery';

const GET_CUSTOM_EVENTS_URL = '/mappings/customEvents';
const DEFAULT_ITERABLE_EVENT = `iterable_purchase`;
const CACHE_KEY = 'iterableLocalStorageCustomEvents';
let cacheInitialized = false;

type CustomEventsResponse = {
  fieldName: string;
  fieldType: string;
  projectId: number;
  dataType: string;
  createdAt: string;
  isHidden: boolean;
  isCustomEvent: boolean;
  seenIfManuallyCreated?: boolean;
  isAllowed?: boolean;
  isSaved?: boolean;
};

type FilterAttributes = Partial<Pick<CustomEventsResponse, 'isAllowed' | 'isHidden' | 'isSaved'>>;
type CustomEventRestQueryOptions = Omit<RestQueryHookOptions, 'enabled'>;

// Default values for event attributes when not explicitly specified in an event
const defaultEventValues: FilterAttributes = {
  isHidden: true,
  isAllowed: true,
  isSaved: true,
};

/**
 * Adds an element to an array if it does not already exist in the array.
 * @param {string[]} array - The array to add the element to.
 * @param {string} elem - The element to add.
 * @returns {string[]} The updated array.
 */
const addIfNotExists = (array: string[], elem: string) =>
  array.includes(elem) ? array : [elem, ...array];

/**
 * Fetches custom events from the server and caches them in localStorage.
 * @returns {CustomEventsResponse[]} An array of custom events.
 */
export const useFetchCustomEvents = (queryOptions?: CustomEventRestQueryOptions) => {
  const [events, setEvents] = useState<CustomEventsResponse[]>([]);
  const [shouldFetch, setShouldFetch] = useState<boolean>(!cacheInitialized);
  const customEventsResponse = useRestQuery<CustomEventsResponse[]>(
    GET_CUSTOM_EVENTS_URL,
    undefined,
    { ...queryOptions, enabled: shouldFetch },
  );
  const addErrorSignal = useRumError();

  useEffect(() => {
    if (!customEventsResponse?.data) return;

    if (!shouldFetch) {
      const cachedEvents = localStorage.getItem(CACHE_KEY);
      if (cachedEvents) {
        setEvents(JSON.parse(cachedEvents));
        return;
      }
    }

    const fetchData = () => {
      const { data: customEvents = [] } = customEventsResponse;

      setEvents(customEvents);
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(customEvents));
        cacheInitialized = true;
        setShouldFetch(false);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : `${error}`;
        addErrorSignal(new Error(errorMessage), 'FETCH_CUSTOM_EVENTS', { error });
      }
    };

    fetchData();
  }, [shouldFetch, customEventsResponse?.data]);

  return events;
};

/**
 * Filters custom events based on the provided filter attributes.
 * @param {FilterAttributes} filterAttributes - The attributes to filter the events by.
 * @returns {CustomEventsResponse[]} The filtered array of custom events.
 */
export const useFilterCustomEvents = (
  filterAttributes: FilterAttributes = {},
  queryOptions?: CustomEventRestQueryOptions,
) => {
  const events = useFetchCustomEvents(queryOptions);
  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      return Object.entries(filterAttributes)
        .filter(([, value]) => value !== undefined)
        .every(
          ([key, value]) =>
            (event[key as keyof CustomEventsResponse] ??
              defaultEventValues[key as keyof FilterAttributes]) === value,
        );
    });
  }, [events, filterAttributes]);

  return filteredEvents;
};

/**
 * Retrieves custom events that are allowed and saved.
 * @returns {string[]} An array of custom events.
 */
export const useAllowedCustomEvents = (queryOptions?: CustomEventRestQueryOptions) => {
  const allowedCustomEvents = useFilterCustomEvents(
    { isAllowed: true, isSaved: true },
    queryOptions,
  );
  return addIfNotExists(
    allowedCustomEvents.map((event) => event.fieldName),
    DEFAULT_ITERABLE_EVENT,
  ).sort();
};

/**
 * Retrieves custom event options that are allowed and saved, formatted for use in a select component.
 * @returns {BaseOption[]} An array of formatted custom event options.
 */
export const useAllowedCustomEventOptions = (queryOptions?: CustomEventRestQueryOptions) => {
  const allowedCustomEvents = useAllowedCustomEvents(queryOptions);
  return transformToBaseOptions(allowedCustomEvents);
};
